/**
 * Search
 */

import {
	getElementFromSelector,
	executeAfterTransition,
	triggerReflow
} from '@ifabnpm/bootstrap/utils.js';
import {
	lockBodyScrolling,
	unlockBodyScrolling
} from '@ifabnpm/bootstrap/utils/scroll.js';
import {getTabbableElements} from '@ifabnpm/bootstrap/utils/tabbable.js';
import FocusTrap from '@ifabnpm/bootstrap/utils/focustrap.js';

import SelectorEngine from '@ifabnpm/bootstrap/dom/selector-engine.js';
import Manipulator from '@ifabnpm/bootstrap/dom/manipulator.js';
import EventHandler from '@ifabnpm/bootstrap/dom/event-handler.js';
import Data from '@ifabnpm/bootstrap/dom/data.js';

// -------
// Privat
// -------

const NAME      = 'search';
const DATA_KEY  = `ifab.${NAME}`;
const EVENT_KEY = `.${DATA_KEY}`;
// const API_KEY    = '.data-api';

let originalActiveElement;
let dialogTrigger;
let dialogContainer;
let searchForm;
let dialogFocustrap;

const setupForm = () => {
	const button = searchForm ? SelectorEngine.findOne('button[type="submit"]', searchForm) : null;
	const field = searchForm ? SelectorEngine.findOne('input[type="search"], input[type="text"]', searchForm) : null;

	if (!button || !field) {
		return;
	}

	const searchStorage = sessionStorage.getItem(`${window.pageInstance.prefix}search`);
	const storageSearchData = (searchStorage) ? JSON.parse(searchStorage) : {};
	const fieldMinLength = parseInt(field.getAttribute('minlength') ?? 3, 10);

	if (storageSearchData.phrase) {
		field.value = storageSearchData.phrase.trim();
	}

	// Formular-Submit prüfen.
	EventHandler.on(searchForm, 'submit', event => {
		if (Data.get(searchForm, 'searchEnabled') !== true) {
			event.preventDefault();
			event.stopPropagation();
		} else {
			storageSearchData.phrase = field.value.trim();

			sessionStorage.setItem(`${window.pageInstance.prefix}search`, JSON.stringify(storageSearchData));
		}
	});

	// Formular prüfen auf aktiv oder inaktiv.
	EventHandler.on(searchForm, 'search.handleSubmit', () => {
		if (field.value !== '' && field.value.length >= fieldMinLength) {
			Data.set(searchForm, 'searchEnabled', true);
			Manipulator.setDisabled(button, false);
		} else {
			Data.set(searchForm, 'searchEnabled', false);
			Manipulator.setDisabled(button);
		}
	});

	// Eingabefeld: bei Eingabe Formular prüfen
	EventHandler.on(field, 'keyup', () => {
		EventHandler.trigger(searchForm, 'search.handleSubmit');
	});

	// Eingabefeld: wenn per Klick ´geleert´, dann Formular prüfen
	EventHandler.on(field, 'clearable.clear', () => {
		EventHandler.trigger(searchForm, 'search.handleSubmit');
	});

	// Formular prüfen
	EventHandler.trigger(searchForm, 'search.handleSubmit');

	window.setTimeout(() => {
		EventHandler.trigger(field, 'clearable.check');
	}, 500);

	// Wenn wir uns auf der Suchseite befinden, muss der Seitentitel um den Suchbegriff erweitert werden.
	if (Manipulator.getDataAttribute(document.body, 'page-type') === 'search') {
		const pageTitleSubheader = SelectorEngine.findOne('.page-title .page-title__subheader') ?? null;

		if (pageTitleSubheader && field.value !== '') {
			pageTitleSubheader.dataset.searchQuery = field.value.trim();
		}
	}
};

const setupDialog = () => {
	//
	// Trigger
	//

	EventHandler.on(dialogTrigger, `on${EVENT_KEY}`, (event) => {
		event.preventDefault();
		event.stopPropagation();

		event.target.setAttribute('aria-pressed', 'true');
	});

	// Trigger-Event: Menütrigger inaktiv
	EventHandler.on(dialogTrigger, `off${EVENT_KEY}`, (event) => {
		event.preventDefault();
		event.stopPropagation();

		event.target.removeAttribute('aria-pressed', 'false');
	});

	EventHandler.on(dialogTrigger, 'click', (event) => {
		event.preventDefault();
		event.stopPropagation();

		const cmd = (event.target.getAttribute('aria-pressed') === 'true') ? 'hide' : 'show';

		EventHandler.trigger(dialogContainer, `${cmd}${EVENT_KEY}`);
	});

	//
	// Dialog
	//

	dialogContainer.setAttribute('tabindex', '-1');
	dialogContainer.style.display = 'none';

	// ... öffnen.
	EventHandler.on(dialogContainer, `show${EVENT_KEY}`, () => {
		originalActiveElement = document.activeElement

		// Trigger ´on´.
		EventHandler.trigger(dialogTrigger, `on${EVENT_KEY}`);

		Manipulator.setAria(dialogContainer, 'hidden', false);

		dialogContainer.style.display = 'block';

		// Scrollen des Dokumentes sperren.
		lockBodyScrolling(dialogContainer);

		dialogFocustrap = new FocusTrap(dialogContainer);

		// Wenn der Dialog angezeigt wird, versucht Safari automatisch auf das erste Element mit dem Attribut
		// `autofocus` den Focus zu setzen. Dies kann zu unschönen visuellen Erscheinungen führen. Von daher wird
		// `autofocus` entfernt und nach dem Öffnen des Drawer wieder reintegriert.
		const elementAutoFocus = SelectorEngine.findOne('[autofocus]', dialogContainer);

		if (elementAutoFocus) {
			elementAutoFocus.removeAttribute('autofocus');
		}

		triggerReflow(dialogContainer);

		Manipulator.addClass(dialogContainer, '-show');

		executeAfterTransition(
			() => {
				dialogFocustrap.activate();

				// Attribute `autofocus` reintegrieren.
				if (elementAutoFocus) {
					elementAutoFocus.setAttribute('autofocus', '');
				}

				const dialogTabbableElements = getTabbableElements(dialogContainer);

				if (dialogTabbableElements.length > 0) {
					dialogTabbableElements[0].focus({preventScroll: true});
				}

				EventHandler.on(dialogContainer, `keydown.dismis${EVENT_KEY}`, (event) => {
					if (event.key !== 'Escape') {
						return;
					}

					EventHandler.trigger(dialogContainer, `hide${EVENT_KEY}`);
				});
			},
			dialogContainer
		);
	});

	// ... schließen.
	EventHandler.on(dialogContainer, `hide${EVENT_KEY}`, () => {
		// Trigger ´off´.
		EventHandler.trigger(dialogTrigger, `off${EVENT_KEY}`);

		dialogFocustrap.deactivate();
		dialogFocustrap = null;

		EventHandler.off(dialogContainer, `keydown.dismis${EVENT_KEY}`);

		// Wenn geschlossen (Transition abwarten).
		executeAfterTransition(() => {
			Manipulator.setAria(dialogContainer, 'hidden', true);

			dialogContainer.style.display = 'none';

			// Scrollen des Dokumentes entsperren.
			unlockBodyScrolling(dialogContainer);
		}, dialogContainer);

		Manipulator.removeClass(dialogContainer, '-show');

		if (originalActiveElement && typeof originalActiveElement.focus === 'function') {
			setTimeout(() => {
				originalActiveElement.focus();

				originalActiveElement = null;
			});
		}
	});

	// ... Klickevents auf/innerhalb der Suchbar nicht ´durchreichen´.
	EventHandler.on(dialogContainer, 'click', (event) => {
		event.stopPropagation();
	});
};

// -------
// Public
// -------

const search = () => {
	dialogTrigger = SelectorEngine.findOne('[aria-controls="page-search"]');
	dialogContainer = dialogTrigger ? getElementFromSelector(dialogTrigger) : null;
	searchForm = dialogContainer ? SelectorEngine.findOne('form', dialogContainer) : null;

	if (dialogTrigger && dialogContainer) {
		setupForm();
		setupDialog();
	} else {
		if (dialogTrigger) {
			Manipulator.setDisabled(dialogTrigger);
		}
	}

	// searchBarTriggers = searchBarTriggers || SelectorEngine.find('[aria-controls="page-search"]');
	//
	// if (searchBarTriggers.length) {
	// 	searchBarContainer = searchBarContainer || getElementFromSelector(searchBarTriggers[0]);
	// }
	//
	// // Keine Trigger und/oder kein Container vorhanden.
	// if(!searchBarTriggers || searchBarTriggers.length < 1 || !searchBarContainer) {
	// 	return;
	// }
	//
	// // Eingabefeld
	// const searchField = SelectorEngine.findOne('.search-box__input', searchBarContainer);
	//
	// //
	// // Events: Suchbar
	// //
	//
	// // Click-Events auf/innerhalb der Suchbar nicht ´durchreichen´.
	// EventHandler.on(searchBarContainer, 'click.ifab.pageSearch', (ev) => {
	// 	ev.stopPropagation();
	// });
	//
	// // ... öffnen.
	// EventHandler.on(searchBarContainer, 'show.ifab.pageSearch', () => {
	// 	searchBarOpen();
	//
	// 	// Suchfeld fokussieren.
	// 	if (searchField) {
	// 		searchField.focus();
	// 	}
	// });
	//
	// // ... schließen.
	// EventHandler.on(searchBarContainer, 'hide.ifab.pageSearch', () => {
	// 	// Suchfeld leeren
	// 	if (searchField) {
	// 		searchField.value = '';
	// 	}
	//
	// 	searchBarClose();
	// });
	//
	// //
	// // Events: Trigger
	// //
	//
	// for (const trigger of searchBarTriggers) {
	// 	EventHandler.on(trigger, 'click.ifab.pageSearchTrigger', (ev) => {
	// 		const cmd = (Data.get(searchBarContainer, 'ifab.searchActive')) ? 'hide' : 'show';
	//
	// 		ev.preventDefault();
	// 		ev.stopPropagation();
	//
	// 		EventHandler.trigger(searchBarContainer, `${cmd}.ifab.pageSearch`);
	// 	});
	// }
	//
	// return searchBarContainer;
};

// Export
export default search;
